import React, { useState, useEffect } from 'react';
import { getCargoSettings, saveCargoSettings, syncPendingCargo } from '../../service/cargo';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { InputSwitch } from 'primereact/inputswitch';
import { Save, Truck, Box, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { IconTextButton } from '../../components/ButtonComponents';

interface CargoSetting {
    id?: number;
    provider: string;
    isActive: boolean;
    username: string; // GÖ Kullanıcı Adı / Sevkiyat Kullanıcı Adı
    password: string; // GÖ Şifre / Sevkiyat Şifre
    apiKey?: string;  // AÖ Kullanıcı Adı / Aras Müşteri Kodu
    apiSecret?: string; // AÖ Şifre
    apiUrl: string;
    extraConfig?: string;
}

export const CargoIntegrationSettings = () => {
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);

    const defaultSettings: CargoSetting[] = [
        { provider: 'YURTICI', isActive: false, username: '', password: '', apiKey: '', apiSecret: '', apiUrl: '' },
        { provider: 'HEPSIJET', isActive: false, username: '', password: '', apiUrl: '' },
        { provider: 'ARAS', isActive: false, username: '', password: '', apiKey: '', apiUrl: '', extraConfig: '' }
    ];

    const [settings, setSettings] = useState<CargoSetting[]>(defaultSettings);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const data = await getCargoSettings();
            if (data && Array.isArray(data) && data.length > 0) {
                const mergedSettings = defaultSettings.map(def => {
                    const found = data.find((d: any) => d.provider === def.provider);
                    return found ? { ...def, ...found } : def;
                });
                setSettings(mergedSettings);
            } else {
                setSettings(defaultSettings);
            }
        } catch (error) {
            console.error('Kargo ayarları yüklenirken hata:', error);
            setSettings(defaultSettings);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (providerSetting: CargoSetting) => {
        try {
            setLoading(true);
            await saveCargoSettings(providerSetting);
            toast.success(`${providerSetting.provider === 'YURTICI' ? 'Yurtiçi Kargo' : providerSetting.provider === 'ARAS' ? 'Aras Kargo' : 'Hepsijet'} ayarları başarıyla kaydedildi.`);
        } catch (error: any) {
            console.error('Kargo ayarları kaydedilirken hata:', error);
            toast.error(error?.response?.data?.message || 'Kaydedilirken hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const handleSyncAllPending = async () => {
        try {
            setSyncing(true);
            const res = await syncPendingCargo();
            toast.success(res.message || 'Tüm bekleyen kargolar başarıyla senkronize edildi.');
        } catch (error: any) {
            console.error('Kargo senkronizasyon hatası:', error);
            toast.error(error?.response?.data?.message || 'Kargolar senkronize edilirken hata oluştu.');
        } finally {
            setSyncing(false);
        }
    };

    const handleFieldChange = (provider: string, field: keyof CargoSetting, value: any) => {
        setSettings(prev => prev.map(s => s.provider === provider ? { ...s, [field]: value } : s));
    };

    // Aras Kargo Raporlama (extraConfig) yardımcıları
    const getArasReportConfig = (extraConfig?: string) => {
        try {
            if (extraConfig) {
                const parsed = JSON.parse(extraConfig);
                return {
                    reportUserName: parsed.reportUserName || '',
                    reportPassword: parsed.reportPassword || ''
                };
            }
        } catch (e) { }
        return { reportUserName: '', reportPassword: '' };
    };

    const updateArasReportConfig = (field: 'reportUserName' | 'reportPassword', value: string) => {
        setSettings(prev => prev.map(s => {
            if (s.provider !== 'ARAS') return s;
            const current = getArasReportConfig(s.extraConfig);
            const updated = { ...current, [field]: value, customerCode: s.apiKey || '' };
            return { ...s, extraConfig: JSON.stringify(updated) };
        }));
    };

    const yurtici = settings.find(s => s.provider === 'YURTICI') || defaultSettings[0];
    const hepsijet = settings.find(s => s.provider === 'HEPSIJET') || defaultSettings[1];
    const aras = settings.find(s => s.provider === 'ARAS') || defaultSettings[2];
    const arasReportConfig = getArasReportConfig(aras.extraConfig);

    return (
        <div className="p-4 space-y-6">
            {/* Header */}
            <div className="hidden md:flex items-center justify-between sticky top-0 z-50 bg-bgAlt backdrop-blur-md -mx-4 -mt-4 p-3 border-b border-borderColor shadow-sm mb-6 rounded-t-lg">
                <div className="flex flex-col">
                    <h3 className="font-bold text-sm text-text md:block">Kargo Entegrasyonları</h3>
                    <p className="text-[11px] text-text opacity-60 hidden md:block">Siparişleriniz için kargo firması API bağlantılarını yönetin.</p>
                </div>
                <div>
                    <IconTextButton
                        icon={RefreshCw}
                        className="btn-new text-xs shadow-sm"
                        onClick={handleSyncAllPending}
                        disabled={syncing || loading}
                    >
                        {syncing ? 'Kargolar Senkronize Ediliyor...' : 'Tüm Bekleyen Kargoları Şimdi Senkronize Et'}
                    </IconTextButton>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

                {/* ARAS KARGO KARTI */}
                <div className="card2 p-0 overflow-hidden flex flex-col">
                    <div className="bg-primary/5 p-4 border-b border-borderColor flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center border border-borderColor">
                                <Truck className="text-primary" size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-sm text-text">Aras Kargo</h3>
                                <p className="text-[10px] opacity-60">Sevkiyat & XML Raporlama Entegrasyonu</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-bold opacity-70">Aktif:</span>
                            <InputSwitch
                                checked={aras.isActive}
                                onChange={(e) => handleFieldChange('ARAS', 'isActive', e.value)}
                            />
                        </div>
                    </div>

                    <div className="p-4 space-y-4 flex-1">
                        {/* 1. Sevkiyat Bilgileri (Kargo Açma) */}
                        <div>
                            <span className="text-[11px] font-bold text-primary uppercase tracking-wider block mb-2">1. Sevkiyat Bilgileri (Kargo Açma)</span>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold opacity-80">Sevkiyat Kullanıcı Adı</label>
                                    <InputText
                                        value={aras.username || ''}
                                        onChange={(e) => handleFieldChange('ARAS', 'username', e.target.value)}
                                        placeholder="Örn: gk7\JUs-spW9"
                                        className="p-inputtext-sm"
                                    />
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold opacity-80">Sevkiyat Şifresi</label>
                                    <Password
                                        value={aras.password || ''}
                                        onChange={(e: any) => handleFieldChange('ARAS', 'password', e.target.value)}
                                        toggleMask
                                        feedback={false}
                                        placeholder="Web Servis Şifresi"
                                        inputClassName="p-inputtext-sm w-full"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 2. Raporlama & Takip (Takip No & Durum) */}
                        <div className="pt-3 border-t border-borderColor">
                            <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider block mb-2">2. Raporlama Bilgileri (Gerçek Takip No & Durum)</span>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold opacity-80">Müşteri Kodu (CustomerCode)</label>
                                    <InputText
                                        value={aras.apiKey || ''}
                                        onChange={(e) => {
                                            handleFieldChange('ARAS', 'apiKey', e.target.value);
                                            updateArasReportConfig('reportUserName', arasReportConfig.reportUserName);
                                        }}
                                        placeholder="Örn: 2603402321149"
                                        className="p-inputtext-sm"
                                    />
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold opacity-80">Raporlama Kullanıcı Adı</label>
                                    <InputText
                                        value={arasReportConfig.reportUserName}
                                        onChange={(e) => updateArasReportConfig('reportUserName', e.target.value)}
                                        placeholder="Örn: hy93TO\1KD@b"
                                        className="p-inputtext-sm"
                                    />
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold opacity-80">Raporlama Şifresi</label>
                                    <Password
                                        value={arasReportConfig.reportPassword}
                                        onChange={(e: any) => updateArasReportConfig('reportPassword', e.target.value)}
                                        toggleMask
                                        feedback={false}
                                        placeholder="Örn: 5kSD\V3O8dH8"
                                        inputClassName="p-inputtext-sm w-full"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5 pt-2 border-t border-borderColor">
                            <label className="text-xs font-bold opacity-80">WSDL URL (Opsiyonel)</label>
                            <InputText
                                value={aras.apiUrl || ''}
                                onChange={(e) => handleFieldChange('ARAS', 'apiUrl', e.target.value)}
                                placeholder="Boş bırakılırsa canlı adres kullanılır"
                                className="p-inputtext-sm text-xs"
                            />
                        </div>
                    </div>

                    <div className="p-3 bg-bgAlt border-t border-borderColor flex justify-end">
                        <IconTextButton
                            icon={Save}
                            className="btn-new shadow-sm"
                            onClick={() => handleSave(aras)}
                            disabled={loading}
                        >
                            Aras Kargo Ayarlarını Kaydet
                        </IconTextButton>
                    </div>
                </div>

                {/* YURTİÇİ KARGO KARTI */}
                <div className="card2 p-0 overflow-hidden flex flex-col">
                    <div className="bg-primary/5 p-4 border-b border-borderColor flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center border border-borderColor">
                                <Truck className="text-primary" size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-sm text-text">Yurtiçi Kargo (KOPS)</h3>
                                <p className="text-[10px] opacity-60">KOPS Web servis entegrasyonu</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-bold opacity-70">Aktif:</span>
                            <InputSwitch
                                checked={yurtici.isActive}
                                onChange={(e) => handleFieldChange('YURTICI', 'isActive', e.value)}
                            />
                        </div>
                    </div>

                    <div className="p-4 space-y-4 flex-1">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-bold opacity-80">GÖ Kullanıcı Adı</label>
                                <InputText
                                    value={yurtici.username || ''}
                                    onChange={(e) => handleFieldChange('YURTICI', 'username', e.target.value)}
                                    placeholder="Gönderici Ödemeli"
                                    className="p-inputtext-sm"
                                />
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-bold opacity-80">GÖ Şifre</label>
                                <Password
                                    value={yurtici.password || ''}
                                    onChange={(e: any) => handleFieldChange('YURTICI', 'password', e.target.value)}
                                    toggleMask
                                    feedback={false}
                                    placeholder="Gönderici Ödemeli"
                                    inputClassName="p-inputtext-sm w-full"
                                />
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-bold opacity-80 text-blue-600 dark:text-blue-400">AÖ Kullanıcı Adı</label>
                                <InputText
                                    value={yurtici.apiKey || ''}
                                    onChange={(e) => handleFieldChange('YURTICI', 'apiKey', e.target.value)}
                                    placeholder="Alıcı Ödemeli (apiKey alanı)"
                                    className="p-inputtext-sm"
                                />
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-bold opacity-80 text-blue-600 dark:text-blue-400">AÖ Şifre</label>
                                <Password
                                    value={yurtici.apiSecret || ''}
                                    onChange={(e: any) => handleFieldChange('YURTICI', 'apiSecret', e.target.value)}
                                    toggleMask
                                    feedback={false}
                                    placeholder="Alıcı Ödemeli (apiSecret alanı)"
                                    inputClassName="p-inputtext-sm w-full"
                                />
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold opacity-80">API URL (İsteğe Bağlı)</label>
                            <InputText
                                value={yurtici.apiUrl}
                                onChange={(e) => handleFieldChange('YURTICI', 'apiUrl', e.target.value)}
                                placeholder="Varsayılan dışına çıkılacaksa (Test ortamı vb.)"
                                className="p-inputtext-sm"
                            />
                        </div>
                    </div>

                    <div className="p-4 border-t border-borderColor bg-bgAlt/30 flex justify-end">
                        <IconTextButton
                            icon={Save}
                            className="btn-new shadow-sm"
                            onClick={() => handleSave(yurtici)}
                            disabled={loading}
                        >
                            Yurtiçi Kargo Ayarlarını Kaydet
                        </IconTextButton>
                    </div>
                </div>

                {/* HEPSİJET KARTI */}
                <div className="card2 p-0 overflow-hidden flex flex-col">
                    <div className="bg-[#f08b1a]/5 p-4 border-b border-borderColor flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center border border-borderColor">
                                <Box className="text-[#f08b1a]" size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-sm text-text">Hepsijet (Qargomate)</h3>
                                <p className="text-[10px] opacity-60">Qargomate REST entegrasyonu</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-bold opacity-70">Aktif:</span>
                            <InputSwitch
                                checked={hepsijet.isActive}
                                onChange={(e) => handleFieldChange('HEPSIJET', 'isActive', e.value)}
                            />
                        </div>
                    </div>

                    <div className="p-4 space-y-4 flex-1">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold opacity-80">Kullanıcı Adı / Email</label>
                            <InputText
                                value={hepsijet.username}
                                onChange={(e) => handleFieldChange('HEPSIJET', 'username', e.target.value)}
                                placeholder="Qargomate paneline giriş e-posta adresi"
                                className="p-inputtext-sm"
                            />
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold opacity-80">Şifre</label>
                            <Password
                                value={hepsijet.password}
                                onChange={(e: any) => handleFieldChange('HEPSIJET', 'password', e.target.value)}
                                toggleMask
                                feedback={false}
                                placeholder="Qargomate paneli şifresi"
                                inputClassName="p-inputtext-sm w-full"
                            />
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold opacity-80">API URL (İsteğe Bağlı)</label>
                            <InputText
                                value={hepsijet.apiUrl}
                                onChange={(e) => handleFieldChange('HEPSIJET', 'apiUrl', e.target.value)}
                                placeholder="Örn: https://integration.qargomate.com"
                                className="p-inputtext-sm"
                            />
                        </div>
                    </div>

                    <div className="p-4 border-t border-borderColor bg-bgAlt/30 flex justify-end">
                        <IconTextButton
                            icon={Save}
                            className="bg-[#f08b1a] text-white hover:bg-[#d87c17] transition-colors shadow-sm"
                            onClick={() => handleSave(hepsijet)}
                            disabled={loading}
                        >
                            Hepsijet Ayarlarını Kaydet
                        </IconTextButton>
                    </div>
                </div>

            </div>
        </div>
    );
};
